import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { FindAlternatesModal } from '@/components/parts/FindAlternatesModal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useDistributorLookup } from '@/lib/distributors';
import {
  type PartDetail,
  type PartUpdateInput,
  usePartCategories,
  useUpdatePart,
} from '@/lib/parts';
import {
  type PartSupplier,
  useMarkSupplierEol,
  usePromoteSupplier,
  useSuppliers,
} from '@/lib/sourcing';
import {
  type Diff,
  type Severity,
  buildDiffs,
  formatValue,
} from '@/lib/validate-rubric';

interface Props {
  orgSlug: string;
  part: PartDetail;
  supplier: PartSupplier;
  onClose: () => void;
  onApplied: () => void;
}

function severityBadge(severity: Severity): JSX.Element {
  if (severity === 'structural') {
    return (
      <span className="rounded bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700 dark:text-red-400">
        structural
      </span>
    );
  }
  if (severity === 'informational') {
    return (
      <span className="rounded bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-600 dark:text-stone-400">
        informational
      </span>
    );
  }
  return (
    <span className="rounded bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
      commercial
    </span>
  );
}


export function ValidateAgainstDistributorModal({
  orgSlug,
  part,
  supplier,
  onClose,
  onApplied,
}: Props) {
  const lookup = useDistributorLookup(orgSlug);
  const update = useUpdatePart(orgSlug, part.id);
  // Categories are needed so the diff for `category_id` renders as
  // "Resistor › Thick Film" rather than a raw integer. Cached for an
  // hour app-wide so this is essentially free.
  const categories = usePartCategories(orgSlug);

  // Run the lookup once when the modal opens. The dependency on
  // supplier id is stable for the modal's lifetime; the empty-deps
  // is intentional but we use the supplier as a key on render to
  // ensure remounting picks up a new supplier.
  useEffect(() => {
    lookup.mutate({
      distributor_slug: supplier.distributor_slug,
      pn: supplier.distributor_pn,
      preview: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.id]);

  const dto = lookup.data;
  const diffs: Diff[] = useMemo(
    () => (dto ? buildDiffs(part, dto, categories.data) : []),
    [part, dto, categories.data],
  );

  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());

  // Initialize selection from preChecked flags on first DTO arrival.
  useEffect(() => {
    if (!dto) return;
    setSelectedFields(
      new Set(diffs.filter((d) => d.preChecked).map((d) => d.def.partField)),
    );
  }, [dto, diffs]);

  const toggleField = (field: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const apply = async () => {
    const payload: PartUpdateInput = { row_version: part.row_version };
    for (const d of diffs) {
      if (!selectedFields.has(d.def.partField)) continue;
      // PartUpdateInput is a structural type with mostly optional
      // fields — coerce to a plain record so the apply loop can write
      // by key without duplicating the schema's field list. The
      // server validates the keys against PartUpdate's schema anyway.
      // Use applyValue when set (category sends the resolved id even
      // though the diff displays the name); fall back to vendor.
      const value = d.applyValue !== undefined ? d.applyValue : d.vendor;
      (payload as unknown as Record<string, unknown>)[d.def.partField as string] = value;
    }
    await update.mutateAsync(payload);
    onApplied();
  };

  const headerLabel = `${supplier.distributor_name} • ${supplier.distributor_pn}`;

  // Portal to document.body. Same fix as FindAlternatesModal — the
  // drawer's translate-x transform creates a containing block for
  // `position: fixed` descendants, so without this the modal would
  // position relative to the drawer instead of the viewport.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              Validate <span className="font-mono">{part.part_number}</span>
            </h2>
            <p className="text-xs text-stone-500 dark:text-stone-400">
              against {headerLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {lookup.isPending && (
            <p className="flex items-center gap-2 text-sm text-stone-500 dark:text-stone-400">
              <Spinner className="h-4 w-4 text-forge-600 dark:text-forge-400" />
              Fetching latest data from {supplier.distributor_name}…
            </p>
          )}
          {lookup.isError && (
            <NotFoundActionPanel
              error={lookup.error as Error}
              orgSlug={orgSlug}
              part={part}
              supplier={supplier}
              onClose={onClose}
            />
          )}
          {dto && diffs.length === 0 && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              ✓ Nothing to update — every comparable field already matches{' '}
              {supplier.distributor_name}.
            </p>
          )}
          {dto && diffs.length > 0 && (
            <>
              <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
                {diffs.length} field{diffs.length === 1 ? '' : 's'} differ.
                Pick which ones to apply. Structural fields (red) are
                pre-checked off — disagreement there usually means the
                supplier is linked to the wrong part.
              </p>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-stone-500 dark:text-stone-400">
                  <tr>
                    <th className="w-8 pb-2 text-left"></th>
                    <th className="pb-2 text-left font-medium">Field</th>
                    <th className="pb-2 text-left font-medium">Current</th>
                    <th className="pb-2 text-left font-medium">
                      {supplier.distributor_name}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {diffs.map((d) => {
                    const checked = selectedFields.has(d.def.partField);
                    return (
                      <tr
                        key={String(d.def.partField)}
                        className={cn(
                          'border-t border-stone-100 dark:border-stone-800',
                          checked && 'bg-forge-50/50 dark:bg-forge-900/10',
                        )}
                      >
                        <td className="py-1.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              toggleField(d.def.partField as string)
                            }
                            className="h-4 w-4 rounded border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                          />
                        </td>
                        <td className="py-1.5 pr-3">
                          <div className="flex items-center gap-1.5">
                            <span>{d.def.label}</span>
                            {severityBadge(d.def.severity)}
                          </div>
                        </td>
                        <td className="py-1.5 pr-3 text-stone-700 dark:text-stone-300">
                          {formatValue(d.current, d.def)}
                        </td>
                        <td className="py-1.5 font-medium text-stone-900 dark:text-stone-100">
                          {formatValue(d.vendor, d.def)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
          {update.isError && (
            <p className="mt-3 text-sm text-red-600">
              Apply failed: {(update.error as Error).message}
            </p>
          )}
        </div>


        <div className="flex items-center justify-end gap-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-5 py-3">
          <span className="mr-auto text-xs text-stone-500 dark:text-stone-400">
            {selectedFields.size} of {diffs.length} selected
          </span>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={apply}
            disabled={
              update.isPending
              || lookup.isPending
              || selectedFields.size === 0
            }
          >
            {update.isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Applying…
              </>
            ) : (
              `Apply ${selectedFields.size} ${selectedFields.size === 1 ? 'change' : 'changes'}`
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}


/** Rendered when the validate lookup fails (typically 404 because the
 * stored distributor PN is dead). Replaces the bare error string with
 * three actionable next steps so the user can resolve the broken
 * supplier in-flow rather than dismissing and hunting for the right
 * action elsewhere. Non-404 errors fall back to the same plain
 * message for backwards-compatibility. */
function NotFoundActionPanel({
  error,
  orgSlug,
  part,
  supplier,
  onClose,
}: {
  error: Error;
  orgSlug: string;
  part: PartDetail;
  supplier: PartSupplier;
  onClose: () => void;
}) {
  const status = error instanceof ApiError ? error.status : null;
  const isNotFound = status === 404;
  const markEol = useMarkSupplierEol(orgSlug, part.id);
  const promote = usePromoteSupplier(orgSlug, part.id);
  const updatePart = useUpdatePart(orgSlug, part.id);
  const allSuppliers = useSuppliers(orgSlug, part.id);
  const [showAlternates, setShowAlternates] = useState(false);
  const [showPromotePicker, setShowPromotePicker] = useState(false);

  const liveAlternates = (allSuppliers.data ?? [])
    .filter((s) => s.id !== supplier.id && !s.is_eol)
    .sort((a, b) => a.priority - b.priority);

  if (!isNotFound) {
    return (
      <p className="text-sm text-red-600">Lookup failed: {error.message}</p>
    );
  }

  const mpn = part.mfg_pn?.trim() ?? '';
  return (
    <>
      <div className="rounded-md border border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-900/20 px-3 py-2.5 text-sm">
        <p className="font-medium text-red-800 dark:text-red-200">
          {supplier.distributor_name} no longer carries this PN
        </p>
        <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-300/80">
          The stored distributor PN{' '}
          <span className="font-mono">{supplier.distributor_pn}</span>{' '}
          returned 404 from {supplier.distributor_name}. Pick how to
          handle it — none of these silently change your part data.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              markEol.mutate(supplier.id);
            }}
            disabled={markEol.isPending || markEol.isSuccess}
            title="Flag this supplier as dead. Keeps the row for residual-stock tracking; suppresses bulk-refresh attempts on it."
          >
            {markEol.isSuccess
              ? 'Marked ✓'
              : markEol.isPending
                ? 'Marking…'
                : 'Mark this supplier as dead'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowAlternates(true)}
            disabled={!mpn}
            title={
              mpn
                ? 'Search every enabled distributor for this MPN'
                : 'Set the manufacturer part number first'
            }
          >
            Find a replacement
          </Button>
          {liveAlternates.length > 0 && !showPromotePicker && (
            <Button
              variant="ghost"
              onClick={() => setShowPromotePicker(true)}
            >
              Promote alternate →
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              if (
                window.confirm(
                  'Mark this part as EOL? Bulk refresh will skip it. ' +
                    'You can undo from the part drawer.',
                )
              ) {
                updatePart.mutate(
                  { row_version: part.row_version, lifecycle: 'obsolete' },
                  { onSuccess: () => onClose() },
                );
              }
            }}
            disabled={updatePart.isPending}
            title="Mark this whole part EOL — bulk refresh will skip it"
          >
            Mark part EOL
          </Button>
        </div>
        {showPromotePicker && liveAlternates.length > 0 && (
          <div className="mt-2 space-y-1 rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-2">
            <p className="text-xs text-stone-500 dark:text-stone-400">
              Pick the alternate to make primary:
            </p>
            {liveAlternates.map((alt) => (
              <button
                key={alt.id}
                type="button"
                onClick={() => {
                  promote.mutate(alt.id, {
                    onSuccess: () => onClose(),
                  });
                }}
                disabled={promote.isPending}
                className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-50"
              >
                <span className="font-medium">{alt.distributor_name}</span>{' '}
                <span className="font-mono text-stone-600 dark:text-stone-400">
                  {alt.distributor_pn}
                </span>
              </button>
            ))}
          </div>
        )}
        {markEol.isError && (
          <p className="mt-2 text-xs text-red-600">
            Mark-EOL failed: {(markEol.error as Error).message}
          </p>
        )}
        {promote.isError && (
          <p className="mt-2 text-xs text-red-600">
            Promote failed: {(promote.error as Error).message}
          </p>
        )}
      </div>

      {showAlternates && mpn && (
        <FindAlternatesModal
          orgSlug={orgSlug}
          partId={part.id}
          mpn={mpn}
          manufacturer={part.manufacturer ?? null}
          description={part.description ?? null}
          onClose={() => setShowAlternates(false)}
        />
      )}
    </>
  );
}
